/**
 * Colophon Océanne — données source unique.
 *
 * Phase 27.85 SSG — extraction des sections depuis colophon-content.tsx.
 * Le contenu est texte-only (paragraphes + items palette) — pas de JSX
 * complexe. Format `paragraphs: string[]` pour les sections texte, `items`
 * structuré pour la palette.
 *
 * Consommé par :
 *  - colophon-content.tsx (composant React modal + page)
 *  - schema-org.ts (AboutPage Schema enrichi avec articleBody)
 *  - content-html-ssr.ts (Worker template HTML injecté dans <div id="root">)
 */

export type ColophonLang = 'fr' | 'en';

export type ColophonParagraph = string;

export type ColophonPaletteItem = {
  name: string;
  hex: string;
  oklch: string;
  role: string;
};

export type ColophonTextSection = {
  kind: 'text';
  id: string;
  title: string;
  paragraphs: ColophonParagraph[];
};

export type ColophonPaletteSection = {
  kind: 'palette';
  id: string;
  title: string;
  intro: string;
  items: ColophonPaletteItem[];
};

export type ColophonSection = ColophonTextSection | ColophonPaletteSection;

const PALETTE_ITEMS_FR: ColophonPaletteItem[] = [
  {
    name: 'Cream',
    hex: '#FAF6F0',
    oklch: 'oklch(0.97 0.011 80)',
    role: 'fond éditorial neutre chaleureux (#FAF6F0).'
  },
  {
    name: 'Terracotta',
    hex: '#C96546',
    oklch: 'oklch(0.62 0.135 38)',
    role: 'CTA, accents brand, focus rings (#C96546). Phase 23 BOLDER : saturation augmentée pour ressortir sur cream.'
  },
  {
    name: 'Sage',
    hex: '#8FA678',
    oklch: 'oklch(0.69 0.055 122)',
    role: 'accent secondaire éditorial (eyebrows italiques).'
  },
  {
    name: 'Peach',
    hex: '#E8B594',
    oklch: 'oklch(0.78 0.085 38)',
    role: 'tertiaire (chips, statuts).'
  },
  {
    name: 'Rose blush',
    hex: '#FDE9EC',
    oklch: 'oklch(0.94 0.025 5)',
    role: "fusion d'accents (marginalia, surfaces tendres)."
  }
];

const PALETTE_ITEMS_EN: ColophonPaletteItem[] = [
  {
    name: 'Cream',
    hex: '#FAF6F0',
    oklch: 'oklch(0.97 0.011 80)',
    role: 'warm neutral editorial background (#FAF6F0).'
  },
  {
    name: 'Terracotta',
    hex: '#C96546',
    oklch: 'oklch(0.62 0.135 38)',
    role: 'CTAs, brand accents, focus rings (#C96546). Phase 23 BOLDER: increased saturation to stand out on cream.'
  },
  {
    name: 'Sage',
    hex: '#8FA678',
    oklch: 'oklch(0.69 0.055 122)',
    role: 'secondary editorial accent (italic eyebrows).'
  },
  {
    name: 'Peach',
    hex: '#E8B594',
    oklch: 'oklch(0.78 0.085 38)',
    role: 'tertiary (chips, statuses).'
  },
  {
    name: 'Rose blush',
    hex: '#FDE9EC',
    oklch: 'oklch(0.94 0.025 5)',
    role: 'accent fusion (marginalia, tender surfaces).'
  }
];

const SECTIONS_FR: ColophonSection[] = [
  {
    kind: 'text',
    id: 'colophon-direction',
    title: 'Direction éditoriale',
    paragraphs: [
      "Éditrice et conseillère : Océanne Brodeur, inscrite à l'Autorité des marchés financiers du Québec (certificat n° 271218). Cabinet d'attache : iA Groupe financier — Agence Beaubien, 1611 boul. Crémazie Est, bureau 501, Montréal (Québec), H2M 2R9.",
      "Voix éditoriale : chaque phrase de ce site a été écrite à la première personne par Océanne, à partir d'un brief signé. Les mantras répétés — « Sinon je vous le dis aussi », « Mon travail est simple », « Vous décidez » — sont verbatim, non négociables, présents pour vous rappeler la posture du conseil.",
      "Histoires : les témoignages nommés (Sophie, Marie-Pier, etc.) sont des voix composites anonymisées, validées avec d'anciennes clientes. Aucun cas réel n'est divulgué sans consentement écrit. Les vrais cas seront identifiés progressivement après le lancement."
    ]
  },
  {
    kind: 'text',
    id: 'colophon-typographie',
    title: 'Typographie',
    paragraphs: [
      "EB Garamond — serif classique pour les titres (h1, h2, h3), citations et eyebrows italiques. Conçu par Georg Duffner, libre de droits (SIL Open Font License). Self-hosté via @fontsource — pas de tracking Google Fonts. Une seule graisse chargée (400 régulier) pour réduire le poids du bundle.",
      "Manrope Variable — sans-serif moderne pour le corps de texte, boutons et UI. Variable font (graisses 300 à 800 dans un seul fichier woff2), conçue par Mikhail Sharanda. Self-hostée. Corps fixé à 500 (medium) pour un poids confortable sur fond cream contre Garamond serif.",
      "Hiérarchie : titres serif (display), corps sans (lecture), eyebrows italiques (signature). Ligne globale 1.65 sur le corps pour respirer, 1.1 sur les titres pour la densité éditoriale. Largeur max 65ch sur les paragraphes (anti-ligne-infinie)."
    ]
  },
  {
    kind: 'palette',
    id: 'colophon-palette',
    title: 'Palette',
    intro:
      "La palette dite « Sage Garden » a été construite en OKLCH (P3-ready, gamut large) pour éviter les approximations HEX. Chaque couleur a un rôle éditorial précis :",
    items: PALETTE_ITEMS_FR
  },
  {
    kind: 'text',
    id: 'colophon-principes',
    title: 'Principes & voix',
    paragraphs: [
      "Compliance AMF non négociable. Aucun chiffre financier n'est présenté sans qualifier indicatif : « environ 12 000 $ », « souvent moins de 10 jours », « pour la plupart des profils admissibles ». Les garanties dépendent de l'assureur et du contrat — toujours.",
      "Loi 25 du Québec. La case de consentement marketing n'est jamais pré-cochée (la loi l'interdit). Les transferts hors-Québec sont encadrés par l'article 17. Aucune séquence courriel automatique après formulaire — c'est Océanne qui vous répond personnellement.",
      "Pas de promesse de rendement. L'assurance maladie grave n'est pas un placement. Les économies présentées sur la calculatrice AMG sont indicatives, basées sur les barèmes moyens des trois grands assureurs canadiens, et soumises à la souscription effective.",
      "Trois soumissions, toujours. Manuvie, Canada-Vie, Sun Life — comparées devant vous, sans présélection. C'est rare dans l'industrie, c'est la méthode signature Océanne."
    ]
  },
  {
    kind: 'text',
    id: 'colophon-a11y',
    title: 'Accessibilité',
    paragraphs: [
      'Le site vise la conformité WCAG 2.2 niveau AA. Score Lighthouse Accessibilité : 100/100 sur mobile et desktop. Audit Chrome DevTools mensuel.',
      "Contraste : ratios 4.5+ pour le texte normal, 3.0+ pour le large. Cibles tactiles : minimum 24×24 px (recommandé 44×44). Lecteurs d'écran : aria-labels, HTML sémantique, focus trap dans les modaux, restauration du focus à la fermeture.",
      "Préférences moteur : prefers-reduced-motion désactive les animations parallaxe et le scroll lissé. Bilingue FR/EN (français par défaut, Loi 96 du Québec).",
      "Une barrière vous gêne ? Écrivez-moi directement par WhatsApp ou courriel — je corrige."
    ]
  },
  {
    kind: 'text',
    id: 'colophon-stack',
    title: 'Stack technique',
    paragraphs: [
      "Frontend : React 19 + TypeScript strict + Vite 8 (Rolldown) + TanStack Router file-based + View Transitions API native. Tailwind CSS v4 avec @theme inline et tokens OKLCH.",
      "Backend : Cloudflare Workers (API + assets unifiés) avec D1 (base SQLite distribuée pour les leads), R2 (sauvegardes mensuelles), KV (cache articles). 3 crons R2 : purge D1 le 1, export R2 le 8, purge R2 le 15.",
      "Pipeline V6 : formulaire → API Worker → D1 + GHL (CRM) → webhook tracking. 4 couches de défense leads (honeypot + timing + rate limit + validation server-side). Conformité Loi 25.",
      "Observabilité : Sentry SDK installé (no-op silencieux tant que VITE_SENTRY_DSN absent). Core Web Vitals reportés (LCP, INP, CLS).",
      "Studio : ce site a été conçu et codé par Intralys, studio éditorial spécialisé en sites pour conseillers AMF au Québec."
    ]
  }
];

const SECTIONS_EN: ColophonSection[] = [
  {
    kind: 'text',
    id: 'colophon-direction',
    title: 'Editorial direction',
    paragraphs: [
      'Editor and advisor: Océanne Brodeur, registered with the Autorité des marchés financiers of Quebec (certificate no. 271218). Affiliated firm: iA Financial Group — Beaubien Agency, 1611 boul. Crémazie Est, suite 501, Montréal (Québec) H2M 2R9.',
      'Editorial voice: every sentence on this site was written in the first person by Océanne, from a signed brief. The recurring mantras — "Sinon je vous le dis aussi", "My work is simple", "You decide" — are verbatim, non-negotiable, present to remind you of the advisory posture.',
      'Stories: the named testimonials (Sophie, Marie-Pier, etc.) are anonymized composite voices, validated with former clients. No real case is disclosed without written consent. Real cases will be identified progressively after launch.'
    ]
  },
  {
    kind: 'text',
    id: 'colophon-typographie',
    title: 'Typography',
    paragraphs: [
      'EB Garamond — classic serif for titles (h1, h2, h3), quotes and italic eyebrows. Designed by Georg Duffner, royalty-free (SIL Open Font License). Self-hosted via @fontsource — no Google Fonts tracking. Only one weight loaded (400 regular) to reduce bundle weight.',
      'Manrope Variable — modern sans-serif for body text, buttons and UI. Variable font (weights 300 to 800 in a single woff2 file), designed by Mikhail Sharanda. Self-hosted. Body fixed at 500 (medium) for comfortable weight on cream background against Garamond serif.',
      'Hierarchy: serif titles (display), sans body (reading), italic eyebrows (signature). Global line-height 1.65 on body for breathing, 1.1 on titles for editorial density. Max width 65ch on paragraphs (anti-infinite-line).'
    ]
  },
  {
    kind: 'palette',
    id: 'colophon-palette',
    title: 'Palette',
    intro:
      'The palette called "Sage Garden" was built in OKLCH (P3-ready, wide gamut) to avoid HEX approximations. Each color has a precise editorial role:',
    items: PALETTE_ITEMS_EN
  },
  {
    kind: 'text',
    id: 'colophon-principes',
    title: 'Principles & voice',
    paragraphs: [
      'AMF compliance non-negotiable. No financial figure is presented without an indicative qualifier: "around $12,000", "often less than 10 days", "for most eligible profiles". Guarantees depend on the insurer and contract — always.',
      "Quebec Law 25. The marketing consent checkbox is never pre-ticked (the law forbids it). Cross-border transfers are governed by Article 17. No automated email sequence after the form — Océanne personally replies.",
      'No yield promise. Critical illness insurance is not an investment. The savings shown in the AMG calculator are indicative, based on the average rates of the three major Canadian insurers, and subject to effective underwriting.',
      'Three quotes, always. Manulife, Canada Life, Sun Life — compared in front of you, with no pre-selection. Rare in the industry, this is the Océanne signature method.'
    ]
  },
  {
    kind: 'text',
    id: 'colophon-a11y',
    title: 'Accessibility',
    paragraphs: [
      'The site aims for WCAG 2.2 level AA compliance. Lighthouse Accessibility score: 100/100 on mobile and desktop. Monthly Chrome DevTools audit.',
      'Contrast: ratios 4.5+ for normal text, 3.0+ for large. Touch targets: minimum 24×24 px (recommended 44×44). Screen readers: aria-labels, semantic HTML, focus trap in modals, focus restoration on close.',
      'Motor preferences: prefers-reduced-motion disables parallax animations and smooth scrolling. Bilingual FR/EN (French by default, Quebec Law 96).',
      'Hitting a barrier? Write to me directly via WhatsApp or email — I will fix it.'
    ]
  },
  {
    kind: 'text',
    id: 'colophon-stack',
    title: 'Tech stack',
    paragraphs: [
      'Frontend: React 19 + TypeScript strict + Vite 8 (Rolldown) + TanStack Router file-based + native View Transitions API. Tailwind CSS v4 with @theme inline and OKLCH tokens.',
      'Backend: Cloudflare Workers (unified API + assets) with D1 (distributed SQLite for leads), R2 (monthly backups), KV (article cache). 3 R2 crons: D1 purge on the 1st, R2 export on the 8th, R2 purge on the 15th.',
      'V6 pipeline: form → Worker API → D1 + GHL (CRM) → tracking webhook. 4 lead defense layers (honeypot + timing + rate limit + server-side validation). Law 25 compliance.',
      'Observability: Sentry SDK installed (silent no-op while VITE_SENTRY_DSN is missing). Core Web Vitals reported (LCP, INP, CLS).',
      'Studio: this site was designed and coded by Intralys, editorial studio specialized in websites for Quebec AMF advisors.'
    ]
  }
];

export function getColophonSections(lang: ColophonLang): ColophonSection[] {
  return lang === 'fr' ? SECTIONS_FR : SECTIONS_EN;
}
